import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LegalPage from "./LegalPage";
import api from "@/lib/api";

const BRAND = "Manthan Foundation";
const EMAIL = "info@manthanfoundation.in";

const DEFAULT_SECTIONS = [
  {
    id: "overview",
    title: "1. Overview",
    content: (
      <>
        <p><strong>{BRAND}</strong> ("we," "us," or "our") is committed to protecting the privacy and personal information of all individuals who interact with our website, training programs, podcast initiatives, and related services.</p>
        <p>This Privacy Policy explains what information we collect, how we use it, how we protect it, and your rights with respect to your personal information. By using our Website or Services, you consent to the practices described in this policy.</p>
      </>
    ),
  },
  {
    id: "information-collected",
    title: "2. Information We Collect",
    content: (
      <>
        <p>We collect different types of information depending on how you interact with us:</p>

        <p><strong>2.1 Personal Information</strong></p>
        <ul>
          <li>Full name, gender, and age.</li>
          <li>Email address, mobile number, and WhatsApp number.</li>
          <li>City, district, and state of residence.</li>
          <li>Profile photograph (when submitted voluntarily).</li>
        </ul>

        <p><strong>2.2 School and Professional Information</strong></p>
        <ul>
          <li>Name and address of the school or institution.</li>
          <li>Designation, subject, and experience.</li>
          <li>Professional qualifications, awards, and achievements.</li>
          <li>School identification number or board affiliation (where applicable).</li>
        </ul>

        <p><strong>2.3 Training and Programme Information</strong></p>
        <ul>
          <li>Training program registrations and booking records.</li>
          <li>Attendance and participation data.</li>
          <li>Assessment results and feedback submissions.</li>
        </ul>

        <p><strong>2.4 Uploaded Documents and Media</strong></p>
        <ul>
          <li>Permission letters, résumés, and supporting documents submitted during registration.</li>
          <li>Profile photographs and other images submitted voluntarily.</li>
          <li>Video or audio content submitted for podcast participation.</li>
        </ul>

        <p><strong>2.5 Certificate Information</strong></p>
        <ul>
          <li>Certificate details including name, program, date of completion, and unique certificate ID.</li>
          <li>QR code verification data linked to certificates.</li>
        </ul>

        <p><strong>2.6 Technical and Usage Data</strong></p>
        <ul>
          <li>IP address, browser type, and device information.</li>
          <li>Pages visited, time spent, and navigation patterns.</li>
          <li>Cookies and session identifiers.</li>
        </ul>

        <p><strong>2.7 Contact Form Submissions</strong></p>
        <ul>
          <li>Name, email, phone number, and the content of your message when you contact us.</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    content: (
      <>
        <p>We use the information collected for the following purposes:</p>
        <ul>
          <li>To process training registrations, bookings, and programme enrolments.</li>
          <li>To issue, deliver, and verify digital certificates of completion.</li>
          <li>To communicate with you regarding programmes, schedules, updates, and confirmations.</li>
          <li>To facilitate podcast production, featuring, and publication (with your consent).</li>
          <li>To maintain accurate records of participation and completion.</li>
          <li>To improve our Website, programmes, and services based on usage patterns and feedback.</li>
          <li>To comply with legal obligations and resolve disputes.</li>
          <li>To send informational newsletters or programme announcements (you may opt out at any time).</li>
        </ul>
        <p>We will only use your information for purposes that are consistent with the reason it was collected, or where we have a legitimate interest in doing so.</p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "4. Cookies",
    content: (
      <>
        <p>We use cookies and similar tracking technologies to enhance your experience on our Website. Cookies are small text files stored on your device when you visit a website.</p>
        <p>We use the following types of cookies:</p>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for the Website to function correctly, including login sessions and security.</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Website so we can improve it.</li>
          <li><strong>Preference Cookies:</strong> Remember your settings and preferences for a better experience.</li>
        </ul>
        <p>You can control or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of the Website.</p>
      </>
    ),
  },
  {
    id: "data-sharing",
    title: "5. Data Sharing",
    content: (
      <>
        <p>We do not sell, rent, or trade your personal information to third parties. We may share your information in the following limited circumstances:</p>
        <ul>
          <li><strong>Schools and Institutions:</strong> Where a school registers their teachers for a programme, certain participant information may be shared with the school's authorised administrators.</li>
          <li><strong>Service Providers:</strong> Trusted third-party service providers who assist with email delivery, cloud storage, or analytics, under strict data processing agreements.</li>
          <li><strong>Legal Compliance:</strong> Where required by law, court order, or regulatory authority.</li>
          <li><strong>Podcast Publishing:</strong> Podcast content and associated participant information (name, designation, school) may be published publicly on our official platforms with participant consent.</li>
        </ul>
        <p>We do not share personal information with advertisers or unrelated third parties.</p>
      </>
    ),
  },
  {
    id: "data-retention",
    title: "6. Data Retention",
    content: (
      <>
        <p>We retain your personal information for as long as necessary to fulfil the purposes for which it was collected, or as required by law.</p>
        <ul>
          <li>Training and booking records are retained for a minimum of 3 years for audit and compliance purposes.</li>
          <li>Certificate records are retained indefinitely to support ongoing QR verification.</li>
          <li>Podcast content and associated participant data are retained for the duration of publication.</li>
          <li>Account information is retained until deletion is requested or the account is terminated.</li>
        </ul>
        <p>When information is no longer required, it is securely deleted or anonymised.</p>
      </>
    ),
  },
  {
    id: "data-protection",
    title: "7. Data Protection and Security",
    content: (
      <>
        <p>We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, use, disclosure, or destruction. These measures include:</p>
        <ul>
          <li>HTTPS encryption for all data transmitted to and from our Website.</li>
          <li>Access controls limiting personal data access to authorized personnel only.</li>
          <li>Secure storage of passwords using industry-standard hashing techniques.</li>
          <li>Regular review of data handling procedures and security practices.</li>
        </ul>
        <p>While we take every reasonable precaution, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information.</p>
      </>
    ),
  },
  {
    id: "user-rights",
    title: "8. Your Rights",
    content: (
      <>
        <p>You have the following rights regarding your personal information:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
          <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal or operational requirements.</li>
          <li><strong>Objection:</strong> Object to certain uses of your information, such as marketing communications.</li>
          <li><strong>Withdrawal of Consent:</strong> Where processing is based on your consent (e.g., podcast participation), you may withdraw your consent at any time by contacting us. Withdrawal will not affect prior lawful processing.</li>
        </ul>
        <p>To exercise any of these rights, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. We will respond to all requests within a reasonable timeframe.</p>
      </>
    ),
  },
  {
    id: "childrens-privacy",
    title: "9. Children's Privacy",
    content: (
      <>
        <p>Our Services are intended for adults aged 18 and above. We do not knowingly collect personal information from individuals under the age of 18. If we become aware that a minor has provided us with personal information without appropriate consent, we will take steps to delete such information promptly.</p>
        <p>If you believe that a minor has submitted personal information to us, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "10. Third-Party Platforms",
    content: (
      <>
        <p>Our Website may link to or embed content from third-party platforms such as YouTube, social media, or analytics services. These platforms have their own privacy policies that govern how they collect and use your data. We are not responsible for the privacy practices of third-party platforms.</p>
        <p>Where we publish podcast content on platforms such as YouTube or social media, the terms and privacy policies of those platforms also apply.</p>
      </>
    ),
  },
  {
    id: "changes-policy",
    title: "11. Changes to This Policy",
    content: (
      <>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make changes, we will update the "Last Updated" date at the top of this page. We encourage you to review this page periodically.</p>
        <p>Material changes will be communicated through a notice on our Website or via email where appropriate.</p>
      </>
    ),
  },
  {
    id: "contact-us",
    title: "12. Contact Us",
    content: (
      <>
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal information, please contact us:</p>
        <div className="highlight-box">
          <p><strong>{BRAND}</strong></p>
          <p>Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
          <p>Website: <Link to="/contact">Contact Page</Link></p>
        </div>
        <p>We are committed to addressing all privacy concerns promptly and transparently.</p>
      </>
    ),
  },
];

export default function Privacy() {
  const [customContent, setCustomContent] = useState(null);

  useEffect(() => {
    api.get("/cms/legal").then((r) => {
      if (r.data?.privacy_custom) setCustomContent(r.data.privacy_custom);
    }).catch(() => {});
  }, []);

  if (customContent) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-[#0F172A] text-white">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">Privacy Policy</h1>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-gray-600 text-[15px] leading-relaxed legal-content"
          dangerouslySetInnerHTML={{ __html: customContent }} />
        <style>{`.legal-content p{margin-bottom:.75rem}.legal-content ul{list-style:disc;padding-left:1.5rem;margin:.75rem 0}.legal-content h2{font-size:1.15rem;font-weight:600;color:#1e293b;margin:1.5rem 0 .5rem}`}</style>
      </div>
    );
  }

  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="We respect your privacy. This policy explains how Manthan Foundation collects, uses, and protects your personal information."
      lastUpdated="1 July 2025"
      breadcrumb="Privacy Policy"
      sections={DEFAULT_SECTIONS}
    />
  );
}
